import { CompanyIntelCard } from "@/components/CompanyIntelCard";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { clientTypeMeta, companyClassMeta, copyToClipboard } from "@/lib/format";
import { ActivityTimeline } from "@/components/crm/ActivityTimeline";
import OfficesMiniMap from "@/components/OfficesMiniMap";
import UpgradeModal from "@/components/UpgradeModal";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Buildings, MapPin, Envelope, Phone, LinkedinLogo,
  Broadcast, UserCircle, Plus, Globe, PencilSimple, Check, X, ArrowSquareOut,
  Bell, BellRinging, Copy, MapTrifold, Prohibit, ArrowCounterClockwise, Sparkle, Trash, MagnifyingGlass, LockSimple, EyeSlash,
} from "@phosphor-icons/react";

const inputCls = "bg-surface-2 border-border text-fg";
const EXCLUSION_REASONS = ["Agrégateur bruyant", "Hors cible", "Doublon / entité fusionnée", "Concurrent", "Autre"];
const CATEGORIES = [
  { v: "BESOIN", l: "Besoin (opérationnel)" },
  { v: "DÉCISION", l: "Décision (décideur)" },
  { v: "ACHAT", l: "Achat (référencement)" },
];

const normalizeUrl = (w) => {
  const v = (w || "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

function Field({ label, children }) {
  return (<div><label className="overline block mb-1.5">{label}</label>{children}</div>);
}

const CONF_LABEL = { VERY_HIGH: "Très élevée", HIGH: "Élevée", MEDIUM: "Moyenne", LOW: "Faible", NONE: "—" };
const CONF_CLS = {
  VERY_HIGH: "bg-emerald-500/12 text-emerald-300 border-emerald-500/30",
  HIGH: "bg-emerald-500/12 text-emerald-300 border-emerald-500/30",
  MEDIUM: "bg-amber-500/12 text-amber-300 border-amber-500/30",
  LOW: "bg-red-500/12 text-red-300 border-red-500/30",
  NONE: "bg-surface-2 text-muted border-border",
};

function EmailPatternCard({ companyId, onApplied }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ domain: "", pattern_type: "FIRST_DOT_LAST" });

  const load = useCallback(() => {
    api.get(`/admin/companies/${companyId}/email-pattern`).then(({ data }) => setData(data)).catch(() => {});
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const detect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/email-pattern/detect`);
      setData((d) => ({ ...(d || {}), patterns: data.patterns }));
      const n = data.emails_applied || 0;
      toast.success(data.patterns?.length
        ? `Pattern détecté${n ? ` · ${n} email(s) déduit(s)` : ""}`
        : "Aucun pattern détectable (emails perso insuffisants)");
      if (n && onApplied) onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const applyPattern = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/email-pattern/apply`, {});
      const n = data.updated || 0;
      if (n) { toast.success(`${n} email(s) déduit(s) et enregistré(s)`); if (onApplied) onApplied(); }
      else toast.info(data.reason === "no_reliable_pattern"
        ? "Aucun pattern fiable — cliquez « Détecter » d'abord."
        : "Aucun email manquant à déduire.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setApplying(false); }
  };
  const put = async (payload, okMsg) => {
    try {
      const { data } = await api.put(`/admin/companies/${companyId}/email-pattern`, payload);
      setData((d) => ({ ...(d || {}), patterns: data.patterns }));
      if (okMsg) toast.success(`${okMsg}${data.emails_applied ? ` · ${data.emails_applied} email(s) déduit(s)` : ""}`);
      if (data.emails_applied && onApplied) onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (domain) => {
    if (!window.confirm(`Supprimer le pattern pour ${domain} ?`)) return;
    const { data } = await api.delete(`/admin/companies/${companyId}/email-pattern/${encodeURIComponent(domain)}`);
    setData((d) => ({ ...(d || {}), patterns: data.patterns }));
    toast.success("Pattern supprimé");
  };
  const saveEdit = async () => {
    if (!form.domain.trim()) { toast.error("Domaine requis"); return; }
    await put({ domain: form.domain.trim(), pattern_type: form.pattern_type }, "Pattern confirmé");
    setEditOpen(false);
  };

  if (!data) return null;
  const patterns = data.patterns || [];
  const primaryDomain = (data.domains || [])[0] || "";
  const types = data.pattern_types || [];
  const labels = data.pattern_labels || {};

  return (
    <div className="surface rounded-lg p-5 mb-6" data-testid="email-pattern-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="overline flex items-center gap-2"><Sparkle size={14} weight="duotone" className="text-accent" /> Email Pattern</div>
          <div className="text-xs text-muted mt-1 max-w-lg">Convention email apprise depuis les contacts InterKO. Elle permet de construire l'email d'un nouveau décideur sans appeler Prospeo. <span className="text-fg/70">{data.contacts_with_email} contact(s) avec email.</span></div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={detect} disabled={busy} data-testid="email-pattern-detect-btn"
            className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift disabled:opacity-60">
            <MagnifyingGlass size={13} weight="bold" /> {busy ? "Analyse…" : "Détecter"}
          </button>
          <button onClick={applyPattern} disabled={applying} data-testid="email-pattern-apply-btn"
            className="flex items-center gap-1.5 text-xs font-semibold bg-accent text-black px-3 py-1.5 rounded-md hover-lift disabled:opacity-60">
            <Sparkle size={13} weight="bold" /> {applying ? "Déduction…" : "Déduire les emails manquants"}
          </button>
          <button onClick={() => { setForm({ domain: primaryDomain, pattern_type: patterns[0]?.pattern_type || "FIRST_DOT_LAST" }); setEditOpen(true); }}
            data-testid="email-pattern-edit-btn"
            className="flex items-center gap-1.5 text-xs font-semibold border border-border text-fg px-3 py-1.5 rounded-md hover-lift">
            <PencilSimple size={13} /> Définir / éditer
          </button>
        </div>
      </div>
      {patterns.length === 0 ? (
        <div className="text-sm text-muted">Aucun pattern pour l'instant. Cliquez « Détecter » pour l'apprendre depuis les contacts, ou « Définir » pour le saisir manuellement.</div>
      ) : (
        <div className="space-y-2">
          {patterns.map((p) => (
            <div key={p.id} data-testid={`email-pattern-${p.domain}`} className={`border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap ${p.disabled ? "border-border opacity-60" : "border-border"}`}>
              <div className="min-w-0">
                <div className="font-mono text-sm text-fg flex items-center gap-2 flex-wrap">
                  {p.template}
                  {p.is_verified && <span className="text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded bg-accent/15 text-accent border border-accent/30">Confirmé ADMIN</span>}
                  {p.buildable && !p.is_verified && <span className="text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded bg-emerald-500/12 text-emerald-300 border border-emerald-500/30">Actif</span>}
                  {p.disabled && <span className="text-[10px] font-sans px-1.5 py-0.5 rounded bg-surface-2 text-muted border border-border">Désactivé</span>}
                </div>
                <div className="text-xs text-muted mt-1">
                  <span className={`px-1.5 py-0.5 rounded border ${CONF_CLS[p.confidence] || CONF_CLS.NONE}`}>Confiance : {CONF_LABEL[p.confidence] || p.confidence}</span>
                  <span className="ml-2 font-mono">{p.matching_samples} échantillon(s){p.conflicting_samples ? ` · ${p.conflicting_samples} en conflit` : ""}</span>
                  {p.last_confirmed_at && <span className="ml-2">Confirmé le {new Date(p.last_confirmed_at).toLocaleDateString("fr-FR")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!p.is_verified && (
                  <button onClick={() => put({ domain: p.domain, pattern_type: p.pattern_type }, "Pattern confirmé")}
                    data-testid={`email-pattern-confirm-${p.domain}`} title="Confirmer (priorité max)"
                    className="text-xs font-semibold text-emerald-300 border border-emerald-500/40 rounded px-2 py-1 hover-lift">Confirmer</button>
                )}
                <button onClick={() => put({ domain: p.domain, disabled: !p.disabled }, p.disabled ? "Réactivé" : "Désactivé")}
                  data-testid={`email-pattern-toggle-${p.domain}`} title={p.disabled ? "Réactiver" : "Désactiver"}
                  className="p-1.5 rounded text-muted hover:text-amber-400 hover:bg-surface-2">
                  {p.disabled ? <ArrowCounterClockwise size={14} /> : <Prohibit size={14} />}
                </button>
                <button onClick={() => del(p.domain)} data-testid={`email-pattern-delete-${p.domain}`} title="Supprimer"
                  className="p-1.5 rounded text-muted hover:text-red-400 hover:bg-surface-2"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-surface border-border max-w-md" data-testid="email-pattern-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-accent" /> Définir le pattern email</DialogTitle>
            <DialogDescription className="text-muted">Un pattern défini ici est <strong className="text-fg">confirmé ADMIN</strong> (priorité maximale sur la détection auto).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Domaine email"><Input className={inputCls} value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))} placeholder="entreprise.com" data-testid="email-pattern-domain-input" /></Field>
            <Field label="Format">
              <Select value={form.pattern_type} onValueChange={(v) => setForm((f) => ({ ...f, pattern_type: v }))}>
                <SelectTrigger className={inputCls} data-testid="email-pattern-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t} value={t}>{labels[t] || t}@domaine</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setEditOpen(false)} className="border border-border text-fg font-semibold px-5 py-2.5 rounded-md hover-lift">Annuler</button>
            <button onClick={saveEdit} data-testid="email-pattern-save-btn" className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift">Confirmer le pattern</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPaid = !!user?.entitlements?.is_paid;
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [editWeb, setEditWeb] = useState(false);
  const [webVal, setWebVal] = useState("");
  const [savingWeb, setSavingWeb] = useState(false);
  const [watching, setWatching] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [hideBusy, setHideBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [officeFilter, setOfficeFilter] = useState(null);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [excludeForm, setExcludeForm] = useState({ reason: "", note: "", cleanup: "keep" });
  const [excludeBusy, setExcludeBusy] = useState(false);

  const copyEmail = async (email, cid) => {
    const ok = await copyToClipboard(email);
    if (ok) {
      setCopiedId(cid);
      toast.success("Email copié");
      setTimeout(() => setCopiedId((v) => (v === cid ? null : v)), 1800);
    } else {
      toast.error("Impossible de copier l'email");
    }
  };

  const load = useCallback(() => {
    api.get(`/companies/${id}`).then(({ data }) => setData(data)).catch(() => {});
    api.get(`/companies/${id}/watch`).then(({ data }) => setWatching(!!data.watching)).catch(() => {});
    api.get(`/hidden-companies`).then(({ data }) => setHidden((data.hidden || []).some((h) => h.company_id === id))).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggleHide = async () => {
    setHideBusy(true);
    try {
      if (hidden) {
        await api.delete(`/companies/${id}/hide`);
        setHidden(false);
        toast.success("Entreprise restaurée dans vos recherches");
      } else {
        await api.post(`/companies/${id}/hide`, {});
        setHidden(true);
        toast.success("Entreprise masquée pour votre organisation");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setHideBusy(false); }
  };

  const toggleWatch = async () => {
    setWatchBusy(true);
    try {
      const { data } = await api.post(`/companies/${id}/watch`);
      setWatching(!!data.watching);
      toast.success(data.watching
        ? "Entreprise suivie — vous serez alerté de ses nouveaux besoins IT"
        : "Entreprise retirée de votre suivi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setWatchBusy(false); }
  };

  const runExclude = async () => {
    setExcludeBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${id}/exclude-from-signals`, {
        reason: excludeForm.reason, note: excludeForm.note, cleanup: excludeForm.cleanup,
      });
      const extra = data.archived ? ` · ${data.archived} signal(aux) archivé(s)` : data.deleted ? ` · ${data.deleted} signal(aux) supprimé(s)` : "";
      toast.success(`Entreprise exclue des signaux${extra}`);
      setExcludeOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setExcludeBusy(false); }
  };

  const allowSignals = async () => {
    try {
      const { data } = await api.post(`/admin/companies/${id}/allow-signals`);
      const extra = data.restored ? ` · ${data.restored} signal(aux) réactivé(s)` : "";
      toast.success(`Entreprise réautorisée${extra}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveWebsite = async () => {
    setSavingWeb(true);
    try {
      await api.patch(`/admin/companies/${id}`, { website: webVal.trim() });
      toast.success("Site web mis à jour");
      setEditWeb(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingWeb(false); }
  };

  const openAdd = () => {
    setForm({ name: "", title: "", seniority: "Décideur", category: "DÉCISION", email: "", phone: "", linkedin: "", city: "", region: "" });
    setShowAdd(true);
  };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const saveContact = async () => {
    if (!form.name?.trim()) { toast.error("Le nom du décideur est requis."); return; }
    setSaving(true);
    try {
      await api.post("/admin/contacts", { ...form, company_id: id });
      toast.success("Décideur ajouté à l'entreprise");
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>;
  const { company, signals, contacts } = data;
  const activeOffice = officeFilter ? (data.offices || []).find((o) => o.id === officeFilter) : null;
  const shownSignals = officeFilter ? signals.filter((s) => s.office_id === officeFilter) : signals;

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-muted hover:text-fg mb-6">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="surface rounded-lg p-7 mb-6" data-testid="company-detail">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-md flex items-center justify-center shrink-0" style={{ background: `${company.logo_color}1a`, border: `1px solid ${company.logo_color}40` }}>
              <Buildings size={28} weight="duotone" style={{ color: company.logo_color }} />
            </div>
            <div>
            <div className="overline mb-1">{company.sector}</div>
            <h1 className="font-display text-3xl font-bold flex items-center gap-3 flex-wrap">
              {company.name}
              {(() => {
                const ccm = companyClassMeta(company.company_class || (company.client_type === "intermediary" ? "ESN_CONSULTING" : "FINAL_CLIENT"));
                return (
                  <span className="text-xs font-mono font-semibold rounded px-2 py-0.5 border" data-testid="company-class-badge"
                    style={{ color: ccm.color, borderColor: ccm.color + "66", background: ccm.color + "14" }}
                    title={`Classification : ${ccm.label}`}>
                    {ccm.label}
                  </span>
                );
              })()}
              {company.underlying_client && (
                <span className="text-xs font-mono rounded px-2 py-0.5 border bg-surface-2 text-fg/80 border-border" data-testid="company-underlying-client"
                  title="Client final explicitement nommé dans l'annonce">
                  Client : {company.underlying_client}
                </span>
              )}
              {isAdmin && company.signalPolicy === "EXCLUDE" && (
                <span className="text-xs font-mono font-semibold rounded px-2 py-0.5 border bg-amber-500/12 text-amber-300 border-amber-500/40 flex items-center gap-1"
                  data-testid="company-excluded-badge" title="Exclue des signaux — ses futurs besoins ne créent plus de signal">
                  <Prohibit size={12} weight="bold" /> Exclue des signaux
                </span>
              )}
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted font-mono mt-2">
              <span className="flex items-center gap-1.5"><MapPin size={14} /> {[company.city, company.region].filter(Boolean).join(", ") || "—"}</span>
              {company.size && <span>{company.size}</span>}
            </div>
            {/* Site web — toujours affiché (même si non renseigné) */}
            <div className="flex items-center gap-2 text-sm mt-3" data-testid="company-website">
              <Globe size={15} className="text-accent shrink-0" />
              {editWeb ? (
                <div className="flex items-center gap-2">
                  <Input className={`${inputCls} h-8 w-64`} value={webVal} onChange={(e) => setWebVal(e.target.value)}
                    placeholder="ex: entreprise.com" data-testid="company-website-input"
                    onKeyDown={(e) => e.key === "Enter" && saveWebsite()} autoFocus />
                  <button onClick={saveWebsite} disabled={savingWeb} data-testid="company-website-save"
                    className="p-1.5 rounded-md bg-accent text-black hover-lift disabled:opacity-60"><Check size={15} weight="bold" /></button>
                  <button onClick={() => setEditWeb(false)} className="p-1.5 rounded-md border border-border text-muted hover:text-fg"><X size={15} /></button>
                </div>
              ) : company.website ? (
                <>
                  <a href={normalizeUrl(company.website)} target="_blank" rel="noreferrer"
                    className="text-accent hover:underline flex items-center gap-1 font-mono" data-testid="company-website-link">
                    {company.website} <ArrowSquareOut size={12} />
                  </a>
                  {isAdmin && (
                    <button onClick={() => { setWebVal(company.website || ""); setEditWeb(true); }}
                      className="p-1 text-muted hover:text-accent" data-testid="company-website-edit"><PencilSimple size={13} /></button>
                  )}
                </>
              ) : (
                <>
                  <span className="text-muted italic">Site web non renseigné</span>
                  {isAdmin && (
                    <button onClick={() => { setWebVal(""); setEditWeb(true); }} data-testid="company-website-add"
                      className="text-xs text-accent hover:underline flex items-center gap-1"><Plus size={12} weight="bold" /> Ajouter</button>
                  )}
                </>
              )}
            </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isAdmin && (
              company.signalPolicy === "EXCLUDE" ? (
                <button onClick={allowSignals} data-testid="allow-signals-btn"
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-md hover-lift border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                  <ArrowCounterClockwise size={16} weight="bold" /> Réautoriser les signaux
                </button>
              ) : (
                <button onClick={() => { setExcludeForm({ reason: "", note: "", cleanup: "keep" }); setExcludeOpen(true); }} data-testid="exclude-signals-btn"
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-md hover-lift border border-border text-fg hover:border-amber-500/40 hover:text-amber-300">
                  <Prohibit size={16} /> Exclure des signaux
                </button>
              )
            )}
            <button onClick={toggleWatch} disabled={watchBusy} data-testid="watch-company-btn"
              className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-md hover-lift shrink-0 border transition-colors ${watching ? "bg-accent/15 text-accent border-accent/40" : "border-border text-fg hover:border-accent/40"}`}>
              {watching ? <BellRinging size={16} weight="fill" /> : <Bell size={16} />}
              {watching ? "Entreprise suivie" : "Suivre l'entreprise"}
            </button>
            <button onClick={toggleHide} disabled={hideBusy} data-testid="hide-company-btn"
              title="Masquer cette entreprise pour toute votre organisation (réversible)"
              className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-md hover-lift shrink-0 border transition-colors ${hidden ? "bg-rose-500/15 text-rose-300 border-rose-500/40" : "border-border text-muted hover:text-rose-300 hover:border-rose-500/40"}`}>
              {hidden ? <ArrowCounterClockwise size={16} /> : <EyeSlash size={16} />}
              {hidden ? "Restaurer" : "Masquer cette entreprise"}
            </button>
          </div>
        </div>
        <p className="text-fg/90 leading-relaxed mt-5">{company.description}</p>
      </div>

      {isAdmin && <EmailPatternCard companyId={company.id} onApplied={load} />}

      <CompanyIntelCard companyId={company.id} />

      {data.offices?.length > 0 && (
        <div className="mb-6" data-testid="company-offices-section">
          <div className="overline mb-3 flex items-center gap-2">
            <MapTrifold size={14} className="text-accent" /> Implantations &amp; concentration des besoins ({data.offices.length})
            <span className="text-muted normal-case font-normal">— cliquez une implantation pour filtrer les signaux</span>
          </div>
          <OfficesMiniMap offices={data.offices} onSelect={setOfficeFilter} selectedId={officeFilter} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="overline flex items-center gap-2"><Broadcast size={14} className="text-accent" /> Signaux détectés ({shownSignals.length}{officeFilter ? ` / ${signals.length}` : ""})</div>
            {activeOffice && (
              <button onClick={() => setOfficeFilter(null)} data-testid="clear-office-filter"
                className="flex items-center gap-1.5 text-xs font-semibold bg-accent/12 text-accent border border-accent/30 px-2.5 py-1 rounded-full hover:bg-accent/20">
                <MapPin size={12} weight="fill" /> {activeOffice.is_headquarters ? "Siège" : "Zone"} : {activeOffice.city} <X size={12} weight="bold" />
              </button>
            )}
          </div>
          <div className="space-y-3" data-testid="company-signals">
            {shownSignals.length === 0 && (
              <div className="text-sm text-muted" data-testid="company-signals-empty">Aucun signal pour cette zone.</div>
            )}
            {shownSignals.map((s) => (
              <div key={s.id} className="surface rounded-lg p-4 hover-lift cursor-pointer" onClick={() => navigate(`/app/opportunity/${s.id}`)} data-testid={`company-signal-${s.id}`}>
                <div className="font-display font-bold text-fg">{s.role_title}</div>
                <div className="text-xs text-muted font-mono mt-0.5">{s.seniority} · {s.contract_type}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {s.skills.slice(0, 5).map((sk) => (
                    <span key={sk} className="text-[0.65rem] font-mono bg-surface-2 text-muted border border-border px-2 py-0.5 rounded">{sk}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2"><UserCircle size={14} className="text-accent" /> Décideurs & contacts ({data.contacts_count ?? contacts.length})</div>
            {isAdmin && (
              <button onClick={openAdd} data-testid="company-add-contact-btn"
                className="flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift">
                <Plus size={13} weight="bold" /> Ajouter un décideur
              </button>
            )}
          </div>
          <div className="space-y-3" data-testid="company-contacts">
            {data.contacts_locked && (
              <div data-testid="company-contacts-locked">
                <div className="flex items-center gap-3 bg-accent/8 border border-dashed border-accent/30 rounded-lg px-4 py-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-accent/12 border border-accent/25 flex items-center justify-center shrink-0"><LockSimple size={17} weight="duotone" className="text-accent" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-fg text-sm">{data.contacts_count} décideur{data.contacts_count > 1 ? "s" : ""} identifié{data.contacts_count > 1 ? "s" : ""}</div>
                    <p className="text-xs text-muted">Coordonnées (nom, email, téléphone) réservées aux abonnés.</p>
                  </div>
                  <button onClick={() => setShowUpgrade(true)} data-testid="company-contacts-unlock-btn" className="shrink-0 inline-flex items-center gap-1.5 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift text-sm"><LockSimple size={14} weight="fill" /> Débloquer</button>
                </div>
                <div className="space-y-3">
                  {contacts.map((c, i) => (
                    <div key={c.id || i} className="surface rounded-lg p-4" data-testid="contact-teaser">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-surface-2 border border-border flex items-center justify-center shrink-0"><UserCircle size={20} className="text-muted" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="select-none blur-[5px] pointer-events-none text-fg font-semibold w-36 truncate" aria-hidden="true">Jean Dupont</div>
                          <div className="text-sm text-fg font-medium mt-0.5" data-testid={`contact-teaser-title-${i}`}>{c.title || "Décideur"}</div>
                          {c.seniority && <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-accent/12 text-accent">{c.seniority}</span>}
                        </div>
                        <LockSimple size={16} weight="fill" className="text-muted shrink-0" />
                      </div>
                      <div className="mt-3 space-y-1.5 pointer-events-none" aria-hidden="true">
                        <div className="flex items-center gap-2 text-xs text-muted"><Envelope size={13} /><span className="blur-[4px] select-none">prenom.nom@entreprise.fr</span></div>
                        <div className="flex items-center gap-2 text-xs text-muted"><Phone size={13} /><span className="blur-[4px] select-none">06 12 34 56 78</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!data.contacts_locked && contacts.length === 0 && <div className="text-sm text-muted">Aucun décideur enregistré.</div>}
            {!data.contacts_locked && contacts.map((c) => (
              <div key={c.id} className="surface rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-fg">{c.name}</div>
                  <span className={`text-[0.65rem] font-mono px-2 py-0.5 rounded ${c.seniority === "Décideur" ? "bg-accent/15 text-accent border border-accent/30" : "bg-surface-2 text-muted border border-border"}`}>{c.seniority}</span>
                </div>
                <div className="text-sm text-muted mb-2">{c.title}</div>
                {(() => {
                  const off = (data.offices || []).find((o) => o.id === c.office_id);
                  const city = c.city || off?.city || data.company?.city;
                  const region = c.region || off?.region || data.company?.region;
                  return (city || region) ? (
                    <div className="text-xs text-muted/80 mb-2 flex items-center gap-1" data-testid={`company-contact-geo-${c.id}`}>
                      <MapPin size={12} /> {[city, region].filter(Boolean).join(" · ")}
                    </div>
                  ) : null;
                })()}
                <div className="space-y-1 text-xs font-mono">
                  {c.email && (
                    <div className="flex items-center gap-1.5" data-testid={`company-contact-email-row-${c.id}`}>
                      <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-muted hover:text-accent min-w-0"><Envelope size={13} className="shrink-0" /> <span className="truncate">{c.email}</span></a>
                      <button onClick={() => copyEmail(c.email, c.id)} title="Copier l'email" data-testid={`company-contact-copy-email-${c.id}`}
                        className="shrink-0 p-1 rounded text-muted hover:text-accent hover:bg-accent/10 transition-colors">
                        {copiedId === c.id ? <Check size={13} weight="bold" className="text-accent" /> : <Copy size={13} />}
                      </button>
                      {isAdmin && (c.email_source === "COMPANY_PATTERN" || c.email_verification_status === "INFERRED") && (
                        <span data-testid={`company-contact-inferred-${c.id}`} title="Email déduit du pattern entreprise (non vérifié)"
                          className="shrink-0 inline-flex items-center gap-1 text-[9px] font-sans font-semibold px-1.5 py-0.5 rounded bg-amber-500/12 text-amber-300 border border-amber-500/30">
                          <Sparkle size={9} weight="fill" /> déduit
                        </span>
                      )}
                    </div>
                  )}
                  {c.phone && <div className="flex items-center gap-2 text-muted"><Phone size={13} /> {c.phone}</div>}
                  {c.linkedin && (
                    <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 text-muted hover:text-accent w-fit" data-testid={`company-contact-linkedin-${c.id}`}>
                      <LinkedinLogo size={13} className="shrink-0" /> <span>LinkedIn</span> <ArrowSquareOut size={11} className="opacity-70" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <ActivityTimeline companyId={company.id} title="Activité de prospection" />
      </div>

      {isAdmin && (
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-surface border-border max-w-lg" data-testid="company-add-contact-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Ajouter un décideur</DialogTitle>
              <DialogDescription className="text-muted">{company.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Nom"><Input className={inputCls} value={form.name || ""} onChange={upd("name")} data-testid="company-contact-name" /></Field>
              <Field label="Fonction"><Input className={inputCls} value={form.title || ""} onChange={upd("title")} data-testid="company-contact-title" placeholder="ex: Directeur des Études, DSI, Acheteur IT" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <Select value={form.seniority} onValueChange={upd("seniority")}>
                    <SelectTrigger className={inputCls} data-testid="company-contact-seniority"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Décideur", "Opérationnel"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Catégorie">
                  <Select value={form.category} onValueChange={upd("category")}>
                    <SelectTrigger className={inputCls} data-testid="company-contact-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Email"><Input className={inputCls} value={form.email || ""} onChange={upd("email")} data-testid="company-contact-email" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ville"><Input className={inputCls} value={form.city || ""} onChange={upd("city")} data-testid="company-contact-city" placeholder="ex: Lille" /></Field>
                <Field label="Région / Métropole"><Input className={inputCls} value={form.region || ""} onChange={upd("region")} data-testid="company-contact-region" placeholder="ex: Hauts-de-France" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Téléphone"><Input className={inputCls} value={form.phone || ""} onChange={upd("phone")} /></Field>
                <Field label="LinkedIn"><Input className={inputCls} value={form.linkedin || ""} onChange={upd("linkedin")} /></Field>
              </div>
            </div>
            <DialogFooter>
              <button onClick={saveContact} disabled={saving} data-testid="company-contact-save"
                className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full disabled:opacity-60">
                {saving ? "Ajout…" : "Ajouter le décideur"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={excludeOpen} onOpenChange={setExcludeOpen}>
          <DialogContent className="bg-surface border-border max-w-lg" data-testid="company-exclude-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Prohibit size={20} weight="duotone" className="text-amber-400" /> Exclure des signaux</DialogTitle>
              <DialogDescription className="text-muted">
                <strong className="text-fg">{company.name}</strong> — ses futurs signaux seront rejetés à l'ingestion (aucun crédit Prospeo consommé). L'entreprise reste en base.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Field label="Raison (optionnel)">
                <Select value={excludeForm.reason || "__none__"} onValueChange={(v) => setExcludeForm((f) => ({ ...f, reason: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className={inputCls} data-testid="company-exclude-reason"><SelectValue placeholder="Choisir une raison…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Aucune</SelectItem>
                    {EXCLUSION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Note (optionnel)">
                <Textarea className={inputCls} value={excludeForm.note} onChange={(e) => setExcludeForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Contexte de l'exclusion…" data-testid="company-exclude-note" />
              </Field>
              <div>
                <label className="overline block mb-2">Signaux existants de cette entreprise</label>
                <div className="space-y-2">
                  {[
                    { v: "keep", l: "Conserver les signaux existants", d: "Seuls les futurs signaux sont bloqués." },
                    { v: "archive", l: "Archiver les signaux existants", d: "Statut EXCLUDED + masqués des ESN (réversibles)." },
                    { v: "delete", l: "Supprimer les signaux existants", d: "Suppression définitive des signaux actuels." },
                  ].map((o) => (
                    <label key={o.v} className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer ${excludeForm.cleanup === o.v ? "border-accent/50 bg-accent/8" : "border-border hover:border-accent/30"}`}
                      data-testid={`company-exclude-cleanup-${o.v}`}>
                      <input type="radio" name="company-exclude-cleanup" checked={excludeForm.cleanup === o.v}
                        onChange={() => setExcludeForm((f) => ({ ...f, cleanup: o.v }))} className="accent-[#00E5FF] mt-0.5" />
                      <span>
                        <span className="text-sm text-fg block">{o.l}</span>
                        <span className="text-xs text-muted">{o.d}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <button onClick={() => setExcludeOpen(false)} className="border border-border text-fg font-semibold px-5 py-2.5 rounded-md hover-lift">Annuler</button>
              <button onClick={runExclude} disabled={excludeBusy} data-testid="company-exclude-confirm-btn"
                className="bg-amber-500 text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60">
                {excludeBusy ? "Exclusion…" : "Exclure des signaux"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} returnTo={pathname} />
    </div>
  );
}
